import { AliasToken } from 'antd/es/theme/interface';
import type { ThemeConfig } from 'antd';
import {
  dwColor,
  dwControlHeight,
  dwFocus,
  dwFont,
  dwMotion,
  dwOverlay,
  dwRadius,
  dwShadow,
  dwType,
} from './tokens';

/**
 * Motyw antd z tokenów redesignu (`tokens.ts`). Kolory stanów są ciemne (kolor tekstu z triady),
 * bo antd używa ich też jako koloru tekstu w Alert, Tag i Typography; jasne tła i ramki ustawiamy
 * jawnie, zamiast zostawiać je palecie wyliczanej przez antd.
 */
export const appTheme: Partial<AliasToken> = {
  colorPrimary: dwColor.action,
  colorLink: dwColor.action,
  colorLinkHover: dwColor.actionHover,
  colorLinkActive: dwColor.actionActive,
  colorSuccess: dwColor.okFg,
  colorSuccessBg: dwColor.okBg,
  colorSuccessBorder: dwColor.okBorder,
  colorWarning: dwColor.warnFg,
  colorWarningBg: dwColor.warnBg,
  colorWarningBorder: dwColor.warnBorder,
  colorError: dwColor.errFg,
  colorErrorBg: dwColor.errBg,
  colorErrorBorder: dwColor.errBorder,
  colorInfo: dwColor.infoFg,
  colorInfoBg: dwColor.infoBg,
  colorInfoBorder: dwColor.infoBorder,
  colorTextBase: dwColor.ink,
  colorText: dwColor.ink,
  colorTextHeading: dwColor.inkStrong,
  colorTextSecondary: dwColor.ink2,
  colorTextTertiary: dwColor.ink3,
  colorTextDescription: dwColor.ink2,
  colorTextPlaceholder: dwColor.ink3,
  colorTextDisabled: dwColor.inkDisabled,
  colorTextLightSolid: dwColor.onColor,
  colorWhite: dwColor.onColor,
  colorBorder: dwColor.lineStrong,
  colorBorderSecondary: dwColor.line,
  colorSplit: dwColor.lineSoft,
  colorBgLayout: dwColor.surface,
  colorBgContainer: dwColor.surface,
  colorBgElevated: dwColor.surface,
  colorBgMask: dwOverlay,
  colorFillAlter: dwColor.canvas,
  colorBgContainerDisabled: dwColor.canvas,
  controlItemBgHover: dwColor.hover,
  controlItemBgActive: dwColor.selected,
  controlItemBgActiveHover: dwColor.selected,
  fontFamily: dwFont.sans,
  fontFamilyCode: dwFont.mono,
  fontSize: dwType.body.size,
  fontSizeSM: dwType.small.size,
  fontSizeLG: dwType.subsection.size,
  fontSizeHeading1: dwType.pageTitle.size,
  fontSizeHeading2: dwType.section.size,
  fontSizeHeading3: dwType.subsection.size,
  fontSizeHeading4: dwType.body.size,
  fontSizeHeading5: dwType.body.size,
  fontWeightStrong: dwType.strong.weight,
  lineHeight: dwType.body.line / dwType.body.size,
  lineHeightSM: dwType.small.line / dwType.small.size,
  lineHeightHeading1: dwType.pageTitle.line / dwType.pageTitle.size,
  lineHeightHeading2: dwType.section.line / dwType.section.size,
  lineHeightHeading3: dwType.subsection.line / dwType.subsection.size,
  borderRadius: dwRadius.control,
  borderRadiusXS: dwRadius.badge - 2,
  borderRadiusSM: dwRadius.badge,
  borderRadiusLG: dwRadius.panel,
  controlHeight: dwControlHeight.md,
  controlHeightSM: dwControlHeight.sm,
  controlHeightLG: dwControlHeight.lg,
  controlOutline: dwFocus.color,
  controlOutlineWidth: dwFocus.width,
  boxShadowSecondary: dwShadow.popover,
  motionDurationFast: `${dwMotion.fast / 1000}s`,
  motionDurationMid: `${dwMotion.base / 1000}s`,
  motionEaseOut: dwMotion.ease,
};

export const appComponents: ThemeConfig['components'] = {
  Button: {
    colorPrimaryHover: dwColor.actionHover,
    colorPrimaryActive: dwColor.actionActive,
    fontWeight: dwType.strong.weight,
    primaryShadow: dwShadow.control,
    defaultShadow: dwShadow.control,
    dangerShadow: 'none',
    // Przycisk niszczący w pełnym czerwonym `errSolid` (biały tekst 5,6:1), a nie w kolorze tekstu błędu.
    colorError: dwColor.errSolid,
    colorErrorHover: dwColor.errSolidHover,
    colorErrorActive: dwColor.errSolidHover,
  },
  Typography: {
    titleMarginTop: 0,
    titleMarginBottom: 0,
  },
  Table: {
    headerBg: dwColor.surface,
    headerColor: dwColor.ink2,
    headerSplitColor: 'transparent',
    headerBorderRadius: 0,
    borderColor: dwColor.lineSoft,
    rowHoverBg: dwColor.hover,
    rowSelectedBg: dwColor.selected,
    rowSelectedHoverBg: dwColor.selected,
    cellPaddingBlock: 12,
    cellPaddingInline: 12,
    cellPaddingBlockSM: 8,
    cellFontSizeSM: dwType.body.size,
  },
  Menu: {
    itemHeight: dwControlHeight.md,
    itemBorderRadius: dwRadius.control,
    itemColor: dwColor.ink,
    itemHoverBg: dwColor.hover,
    itemSelectedBg: dwColor.selected,
    itemSelectedColor: dwColor.action,
    groupTitleColor: dwColor.ink2,
    groupTitleFontSize: dwType.small.size,
  },
  Tabs: {
    itemColor: dwColor.ink2,
    itemSelectedColor: dwColor.action,
    itemHoverColor: dwColor.ink,
    inkBarColor: dwColor.action,
    titleFontSize: dwType.body.size,
    horizontalItemGutter: 24,
  },
  Tag: {
    defaultBg: dwColor.neutralBg,
    defaultColor: dwColor.neutralFg,
  },
  Modal: {
    titleFontSize: dwType.subsection.size,
    titleLineHeight: dwType.subsection.line / dwType.subsection.size,
  },
  Tooltip: {
    colorBgSpotlight: dwColor.ink2,
  },
};
